import './AppLayout.css';
import { Outlet, useLocation } from 'react-router-dom';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import { AppError } from '../../base/AppError/AppError';
import { Header } from '../../base/Header/Header';
import { Navbar } from '../../base/Navbar/Navbar';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { useTranslation } from 'react-i18next';

const AppLayout = () => {
	const location = useLocation();
	const { merchant } = useContext(appContext as React.Context<AppContext>);

	const { isSuspended, isDemo, isAccountPath } = useMemo(() => {
		const isSuspended = merchant?.status === 'suspended';
		const isDemo = merchant?.status === 'demo';
		const path = location.pathname;
		const isAccountPath =
			path.startsWith('/merchant') ||
			path.startsWith('/billing') ||
			path.startsWith('/settings') ||
			path.startsWith('/user-data');
		return {
			isSuspended,
			isDemo,
			isAccountPath,
		};
	}, [location, merchant]);

	return (
		<div className={`app-layout${isSuspended ? ' app-layout--suspended' : ''}${isDemo ? ' app-layout--demo' : ''}`}>
			<div className='app-route'>
				<Header />
				<Navbar />
				{isSuspended && !isAccountPath ? (
					<SuspendedAlert />
				) : import.meta.env.MODE === 'production' ? (
					<div className='content container'>
						<AppError>
							<Outlet />
						</AppError>
					</div>
				) : (
					<div className='content container'>
						<Outlet />
					</div>
				)}
			</div>
			{isDemo && <DemoBanner />}
		</div>
	);
};

const SuspendedAlert = () => {
	const { t } = useTranslation();

	return (
		<SlAlert className='suspended-alert' variant='danger' open>
			<SlIcon slot='icon' name='exclamation-square-fill' />
			{t('Account suspended message')}
		</SlAlert>
	);
};

const DemoBanner = () => {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const observer = new ResizeObserver((entries) => {
			const height = entries[0].borderBoxSize[0].blockSize;
			document.documentElement.style.setProperty('--demo-banner-height', height + 'px');
		});
		observer.observe(el);

		return () => {
			observer.disconnect();
			document.documentElement.style.setProperty('--demo-banner-height', '0px');
		};
	}, []);

	return (
		<div ref={ref} className='demo-banner'>
			{t('Demo banner message')}
		</div>
	);
};

export { AppLayout };
